import { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Input,
  Button,
  Flex,
  Icon,
  Badge,
  useColorMode,
  useToast,
} from "@chakra-ui/react";
import { FaRocket, FaCoins, FaVideo } from "react-icons/fa";
import { useMutation, gql } from "@apollo/client";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

const REGISTER_FOR_EVENT = gql`
  mutation RegisterForEvent($input: RegisterForEventInput!) {
    registerForEvent(input: $input) {
      success
      message
      registration {
        id
        email
        firstName
      }
    }
  }
`;

const EVENT_ID = "bb-webinar-business-sovereignty";
const EVENT_NAME = "Business Sovereignty: Own Your Data, Train Your AI, Scale Your Way";

const InterestPage = () => {
  const { colorMode } = useColorMode();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const bg = getColor("background.main", colorMode);
  const textPrimary = colorMode === "light" ? "gray.800" : "white";
  const textSecondary = colorMode === "light" ? "gray.600" : "gray.300";

  const [registerForEvent, { loading }] = useMutation(REGISTER_FOR_EVENT);

  const handleSubmit = async () => {
    if (!email || !email.includes("@")) {
      toast({ title: "Please enter a valid email", status: "warning", duration: 3000 });
      return;
    }

    try {
      const { data } = await registerForEvent({
        variables: {
          input: {
            eventId: EVENT_ID,
            eventName: EVENT_NAME,
            email,
            firstName: email.split("@")[0],
            source: "INTEREST_PAGE",
          },
        },
      });

      if (data?.registerForEvent?.success) {
        setSubmitted(true);
        toast({ title: "You're on the list!", status: "success", duration: 5000 });
      }
    } catch (error) {
      toast({ title: "Something went wrong. Try again.", status: "error", duration: 3000 });
    }
  };

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="800px" py={20}>
        <VStack spacing={8} textAlign="center">
          <Badge
            bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)"
            color="white"
            fontSize="lg"
            px={6}
            py={2}
            borderRadius="full"
            fontWeight="600"
            boxShadow="0 4px 16px rgba(147, 51, 234, 0.4)"
          >
            <Icon as={FaVideo} mr={2} />
            Free Live Webinar
          </Badge>

          <Heading
            as="h1"
            fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
            fontWeight="800"
            color={textPrimary}
            lineHeight="1.1"
          >
            Something exciting{" "}
            <Text
              as="span"
              bgGradient="linear(to-r, #9333EA, #F59E0B)"
              bgClip="text"
            >
              is coming.
            </Text>
          </Heading>

          <Text fontSize={{ base: "lg", md: "xl" }} color={textSecondary} maxW="600px">
            Tom Miller is hosting a free live webinar on how to take back control
            of your business data, train AI on YOUR terms, and build true business sovereignty.
          </Text>

          <VStack spacing={3}>
            <Flex align="center" gap={2}>
              <Icon as={FaCoins} color="#F59E0B" />
              <Text color={textSecondary} fontWeight="600">
                Earn 1 BB Token (AU$50) just for registering
              </Text>
            </Flex>
            <Flex align="center" gap={2}>
              <Icon as={FaRocket} color="#9333EA" />
              <Text color={textSecondary} fontWeight="600">
                Early access to our premium setup offer
              </Text>
            </Flex>
          </VStack>

          {!submitted ? (
            <Box
              bg="rgba(147, 51, 234, 0.1)"
              p={8}
              borderRadius="2xl"
              borderWidth="2px"
              borderColor="rgba(147, 51, 234, 0.3)"
              w="100%"
              maxW="500px"
            >
              <Text fontSize="lg" fontWeight="700" color={textPrimary} mb={4}>
                Get notified when registration opens
              </Text>
              <VStack spacing={4}>
                <Input
                  placeholder="Enter your email"
                  size="lg"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  bg={colorMode === "light" ? "white" : "gray.800"}
                  borderColor="rgba(147, 51, 234, 0.3)"
                  _focus={{ borderColor: "#9333EA" }}
                  onKeyPress={(e) => e.key === "Enter" && handleSubmit()}
                />
                <Button
                  size="lg"
                  w="100%"
                  bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)"
                  color="white"
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(147, 51, 234, 0.5)",
                  }}
                  isLoading={loading}
                  onClick={handleSubmit}
                >
                  Yes, I'm Interested!
                </Button>
              </VStack>
            </Box>
          ) : (
            <Box
              bg="rgba(16, 185, 129, 0.1)"
              p={8}
              borderRadius="2xl"
              borderWidth="2px"
              borderColor="rgba(16, 185, 129, 0.3)"
              w="100%"
              maxW="500px"
            >
              <Text fontSize="2xl" mb={2}>
                ✅
              </Text>
              <Heading size="md" color={textPrimary} mb={2}>
                You're on the list!
              </Heading>
              <Text color={textSecondary}>
                We'll email you when registration opens. Keep an eye on your inbox.
              </Text>
            </Box>
          )}

          <Text fontSize="sm" color="gray.500" fontStyle="italic">
            No spam. Just one email when we're ready to go live.
          </Text>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default InterestPage;
